import { api } from '../../api/client';
import type { ArchivedFilter } from '../components/api';
import type { Purchase } from '../purchases/api';
import type { ChildVersionBrief } from '../specs/api';

export type ProjectStatus = 'planned' | 'active' | 'done' | 'cancelled';

export const PROJECT_STATUSES: ProjectStatus[] = ['planned', 'active', 'done', 'cancelled'];

export interface ProjectItem {
  id: string;
  spec_version_id: string;
  units: number;
  note: string | null;
  spec_version: ChildVersionBrief | null;
  /** The version code alone does not say what is being built. */
  specification_name: string | null;
}

export interface Project {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  due_at: string | null;
  note: string | null;
  items: ProjectItem[];
  /** Set when it was taken out of the list, so a row can say so rather than
   *  looking like any other project. */
  archived_at: string | null;
}

export interface DemandRow {
  component_id: string;
  internal_code: string;
  name: string;
  mpn: string | null;
  unit_code: string | null;
  required: string;
  issued: string;
  on_hand: string;
  /** Confirmed and still unreceived purchase-order quantity. */
  ordered: string;
  expected_covered: string;
  timely_expected: string;
  draft_quantity: string;
  deliveries: ExpectedDelivery[];
  draft_deliveries: ExpectedDelivery[];
  fulfillment_component_id: string;
  fulfillment_name: string;
  fulfillment_internal_code: string;
  fulfillment_reserved: string;
  alternative_names: string[];
  fulfillment_options: FulfillmentOption[];
  /** Held by other projects, so it is excluded from this project's free stock. */
  reserved_elsewhere: string;
  reserved: string;
  free: string;
  covered: string;
  /** required − issued − on_hand, floored at zero: what has to be bought. */
  shortage: string;
  suggested_location_id: string | null;
  suggested_location_name: string | null;
  suggested_location_qty: string | null;
}

export interface ExpectedDelivery {
  purchase_id: string;
  purchase_code: string;
  purchase_line_id: string;
  quantity: string;
  expected_at: string | null;
  status: string;
}

export interface FulfillmentOption {
  component_id: string;
  internal_code: string;
  name: string;
  on_hand: string;
  reserved: string;
  reserved_elsewhere: string;
  free: string;
  suggested_location_id: string | null;
  suggested_location_name: string | null;
}

export interface Demand {
  project_id: string;
  include_optional: boolean;
  rows: DemandRow[];
  short_count: number;
}

export interface IssueLine {
  component_id: string;
  location_id: string;
  quantity: string;
}

export interface IssueResult {
  project: Project;
  posted: number;
  /** Pairs driven below zero. A warning, never a refusal. */
  negative: string[];
}

export interface ReservationResult {
  component_id: string;
  requested: string;
  reserved: string;
}

export const listProjects = (status?: ProjectStatus, archived: ArchivedFilter = 'hide') => {
  const query = new URLSearchParams();
  if (status) query.set('status', status);
  if (archived !== 'hide') query.set('archived', archived);
  const suffix = query.toString();
  return api.get<Project[]>(`/projects${suffix ? `?${suffix}` : ''}`);
};

export const restoreProject = (id: string) => api.post<Project>(`/projects/${id}/restore`);

export const getProject = (id: string) => api.get<Project>(`/projects/${id}`);

export const createProject = (input: {
  name: string;
  description?: string | null;
  due_at?: string | null;
  note?: string | null;
}) => api.post<Project>('/projects', input);

export const updateProject = (
  id: string,
  input: { name?: string; status?: ProjectStatus; due_at?: string | null; note?: string | null },
) => api.patch<Project>(`/projects/${id}`, input);

export const archiveProject = (id: string) => api.delete<void>(`/projects/${id}`);

export const addItem = (
  projectId: string,
  input: { spec_version_id: string; units: number; note?: string | null },
) => api.post<ProjectItem>(`/projects/${projectId}/items`, input);

export const updateItem = (projectId: string, itemId: string, input: { units?: number }) =>
  api.patch<ProjectItem>(`/projects/${projectId}/items/${itemId}`, input);

export const deleteItem = (projectId: string, itemId: string) =>
  api.delete<void>(`/projects/${projectId}/items/${itemId}`);

export const getDemand = (projectId: string, includeOptional: boolean) =>
  api.get<Demand>(`/projects/${projectId}/demand?include_optional=${includeOptional}`);

export const setReservation = (
  projectId: string,
  input: {
    component_id: string;
    requirement_component_id?: string;
    quantity: string;
    allow_partial?: boolean;
  },
) => api.put<ReservationResult>(`/projects/${projectId}/reservations`, input);

export const reserveAvailable = (projectId: string, componentIds: string[]) =>
  api.post<ReservationResult[]>(`/projects/${projectId}/reservations/reserve-available`, {
    component_ids: componentIds,
  });

/** The same table as a file. Written by the server, which already owns the one
 *  spreadsheet writer this application has — and which knows the project's code
 *  to name the file after. */
export const demandXlsx = (projectId: string, includeOptional: boolean) =>
  api.blob(`/projects/${projectId}/demand.xlsx?include_optional=${includeOptional}`);

/** Takes the kit off the shelves and tags the movements with the project, which
 *  is what keeps the shortage report right afterwards. */
export const issueKit = (
  projectId: string,
  lines: IssueLine[],
  note?: string | null,
  idempotencyKey?: string,
) =>
  api.post<IssueResult>(`/projects/${projectId}/issue`, {
    lines,
    note: note ?? null,
    idempotency_key: idempotencyKey,
  });

export const draftPurchase = (projectId: string, includeOptional: boolean) =>
  api.post<Purchase>(`/projects/${projectId}/purchase-draft`, {
    include_optional: includeOptional,
  });
